import { z } from 'zod';
import {
  EQUIPMENT_MAX_LEVEL,
  equipmentComponentSchema,
  equipmentInstanceSchema,
  equipmentSlotViewSchema,
  UPGRADE_CURRENCIES,
  UPGRADE_OUTCOMES,
} from '../equipment';
import { decimalStringSchema, huntSessionStateSchema, progressionSchema } from './hunt';

/**
 * The equipment routes (prompt 7A.1).
 *
 * ## What a request may say
 *
 * An instance id, a slot, a currency, whether a stabiliser is on the attempt,
 * and a replay key. Never an account id, never an attribute, never a level,
 * never a value, never a Power, never a price, never a chance. Every number the
 * player sees came from the server and is recomputed there before it is acted
 * on - a client that lies is naming an item it does not own or confirming a
 * quote that no longer exists, and both are refusals.
 *
 * ## Why the answers carry the session
 *
 * Equipping, removing and upgrading all change what the ship is worth RIGHT
 * NOW, and the hunt must not restart to find out. So each answer carries the
 * SAME session, reconfigured in place, with its snapshot and digest - exactly
 * as the loadout and point-allocation answers already do. The arena continues:
 * same mobs, same boss, same countdown, same shots in flight.
 */

export const EQUIPMENT_ROUTE = '/api/equipment' as const;
export const EQUIPMENT_EQUIP_ROUTE = '/api/equipment/equip' as const;
export const EQUIPMENT_UNEQUIP_ROUTE = '/api/equipment/unequip' as const;
export const EQUIPMENT_UPGRADE_PREVIEW_ROUTE = '/api/equipment/upgrade/preview' as const;
export const EQUIPMENT_UPGRADE_ROUTE = '/api/equipment/upgrade' as const;

/* -------------------------------------------------------------------------- */
/*  Listing                                                                   */
/* -------------------------------------------------------------------------- */

export const equipmentStateSchema = z.object({
  /** Always all five, in the fixed component order, empty ones included. */
  slots: z.array(equipmentSlotViewSchema),
  /** Every equipment instance the account owns, equipped ones included. */
  owned: z.array(equipmentInstanceSchema),
  /** How many Quantum Stabilisers are available (total minus reserved). */
  stabilizers: decimalStringSchema,
  /** The version the next equip or removal must carry. */
  loadoutVersion: z.number().int().min(0),
});
export type EquipmentState = z.infer<typeof equipmentStateSchema>;

export const equipmentListResponseSchema = equipmentStateSchema;
export type EquipmentListResponse = z.infer<typeof equipmentListResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  Equip and remove                                                          */
/* -------------------------------------------------------------------------- */

/**
 * The slot is NOT in the request.
 *
 * A component's slot is a property of its definition, so the server infers it.
 * Letting the client choose would be letting it put a Receptor in the Núcleo
 * slot, which is a rule the database would then have to catch.
 */
export const equipmentEquipRequestSchema = z
  .object({
    itemInstanceId: z.uuid(),
    /** Optimistic concurrency: two tabs from the same view, only one winner. */
    expectedLoadoutVersion: z.number().int().min(0),
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export type EquipmentEquipRequest = z.infer<typeof equipmentEquipRequestSchema>;

export const equipmentUnequipRequestSchema = z
  .object({
    component: equipmentComponentSchema,
    expectedLoadoutVersion: z.number().int().min(0),
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export type EquipmentUnequipRequest = z.infer<typeof equipmentUnequipRequestSchema>;

/**
 * What every mutating equipment answer carries about the run in progress.
 *
 * ## There is deliberately no snapshot here
 *
 * This echo used to carry `snapshot` and `snapshotDigest`, read straight from
 * the session row. Nothing consumed them - the inventory panel reads the four
 * equipment fields and discards the rest - and carrying them was a loaded gun:
 * the row is settled to `now` only for an ECONOMIC session, so in a debug run
 * that snapshot still describes the instant the session was created. A
 * consumer who adopted it would take the arena back there, which is exactly
 * the defect fixed for `hunt:pet` in b98d6e2 and for `hunt:reconfigure` in
 * a39dde2, waiting for somebody to wire it up.
 *
 * So it is gone rather than guarded. `HuntSimulation.restore` needs a
 * snapshot; without one this message cannot rewind a hunt however it is used.
 * A change that adds it back fails `equipment-echo.test.ts`.
 *
 * The fields that remain cannot restore anything: `session` identifies the run
 * that is still going, `progression` is the authoritative XP and Gold, and
 * `settled` says what the pending tail earned before the change.
 */
export const equipmentSessionEchoSchema = z.object({
  /** The SAME session, reconfigured. Null when the account was not hunting. */
  session: huntSessionStateSchema.nullable(),
  progression: progressionSchema.nullable(),
  /** What the pending tail earned BEFORE the change, at the old numbers. */
  settled: z
    .object({
      validatedMs: z.number().int().min(0),
      xpEarned: decimalStringSchema,
      goldEarned: decimalStringSchema,
      levelsGained: decimalStringSchema,
    })
    .nullable(),
});

export const equipmentMutationResponseSchema = equipmentStateSchema
  .extend({
    /** True when the same key was already applied; nothing changed again. */
    replayed: z.boolean(),
  })
  .extend(equipmentSessionEchoSchema.shape);
export type EquipmentMutationResponse = z.infer<typeof equipmentMutationResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  Upgrading                                                                 */
/* -------------------------------------------------------------------------- */

export const upgradeCurrencySchema = z.enum(UPGRADE_CURRENCIES);

/**
 * The authoritative quote. The client displays it; it never computes one.
 *
 * Balances travel with it so the confirmation dialog can say "you cannot afford
 * this" without a second request - but affordability is decided again on the
 * server, inside the transaction, against a locked wallet.
 */
export const upgradeQuoteSchema = z.object({
  itemId: z.uuid(),
  currentLevel: z.number().int().min(1).max(EQUIPMENT_MAX_LEVEL),
  targetLevel: z.number().int().min(2).max(EQUIPMENT_MAX_LEVEL),
  successBps: z.number().int().min(0).max(10_000),
  destroysOnFailure: z.boolean(),
  stabilizerDefinitionId: z.string().nullable(),
  /** How many the account has available right now. */
  stabilizersAvailable: decimalStringSchema,
  goldCost: decimalStringSchema,
  cashCost: decimalStringSchema,
  goldBalance: decimalStringSchema,
  cashBalance: decimalStringSchema,
  rarityMultiplierBps: z.number().int().min(10_000),
  costVersion: z.number().int().min(1),
  powerNow: z.number().int().min(1),
  /** What the Power becomes if it succeeds. Never a promise that it will. */
  powerAfterSuccess: z.number().int().min(1),
  /** Why the attempt is impossible, or null when it may be made. */
  blockedReason: z.string().nullable(),
});
export type UpgradeQuoteWire = z.infer<typeof upgradeQuoteSchema>;

export const equipmentUpgradePreviewResponseSchema = z.object({
  quote: upgradeQuoteSchema,
  item: equipmentInstanceSchema,
});
export type EquipmentUpgradePreviewResponse = z.infer<typeof equipmentUpgradePreviewResponseSchema>;

export const equipmentUpgradeRequestSchema = z
  .object({
    itemInstanceId: z.uuid(),
    /**
     * The level the client believes it is upgrading FROM.
     *
     * Not decoration: it is what stops a confirmation dialog opened before
     * another tab succeeded from paying a level-9 price for a level-10 item.
     */
    expectedLevel: z
      .number()
      .int()
      .min(1)
      .max(EQUIPMENT_MAX_LEVEL - 1),
    currency: upgradeCurrencySchema,
    /** Whether to put a Quantum Stabiliser on this attempt. */
    useStabilizer: z.boolean(),
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export type EquipmentUpgradeRequest = z.infer<typeof equipmentUpgradeRequestSchema>;

export const upgradeOutcomeSchema = z.enum(UPGRADE_OUTCOMES);

export const equipmentUpgradeResponseSchema = equipmentStateSchema
  .extend({
    outcome: upgradeOutcomeSchema,
    previousLevel: z.number().int().min(1).max(EQUIPMENT_MAX_LEVEL),
    newLevel: z.number().int().min(1).max(EQUIPMENT_MAX_LEVEL),
    /** The component after the attempt, or null when it was destroyed. */
    item: equipmentInstanceSchema.nullable(),
    stabilizerConsumed: z.boolean(),
    currency: upgradeCurrencySchema,
    amountPaid: decimalStringSchema,
    goldBalance: decimalStringSchema,
    cashBalance: decimalStringSchema,
    replayed: z.boolean(),
  })
  .extend(equipmentSessionEchoSchema.shape);
export type EquipmentUpgradeResponse = z.infer<typeof equipmentUpgradeResponseSchema>;
